if (!customElements.get('byte-inspiration-features')) {
  class ByteInspirationFeatures extends HTMLElement {
    static MOBILE_MAX_WIDTH = 600;

    constructor() {
      super();

      this.activeIndex = 0;
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleScroll = this.handleScroll.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      this.#bindEvents();
      this.#updateState();
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    handleClick(event) {
      if (!this.#isMobileSlider()) return;

      const actionTrigger = event.target.closest('[data-inspiration-features-action]');
      if (!actionTrigger) return;

      const action = actionTrigger.dataset.inspirationFeaturesAction;

      if (action === 'prev') this.#goToSlide(this.activeIndex - 1);
      if (action === 'next') this.#goToSlide(this.activeIndex + 1);
    }

    handleScroll() {
      if (!this.#isMobileSlider()) return;
      this.#syncActiveIndexFromScroll();
    }

    handleResize() {
      this.#updateState();
    }

    #cacheElements() {
      this.track = this.querySelector('[data-inspiration-features-track]');
      this.slides = this.querySelectorAll('[data-inspiration-features-slide]');
      this.prevButton = this.querySelector('[data-inspiration-features-action="prev"]');
      this.nextButton = this.querySelector('[data-inspiration-features-action="next"]');
      this.controls = this.querySelector('[data-inspiration-features-controls]');
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick);
      this.track?.addEventListener('scroll', this.boundHandleScroll, { passive: true });
      window.addEventListener('resize', this.boundHandleResize);
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick);
      this.track?.removeEventListener('scroll', this.boundHandleScroll);
      window.removeEventListener('resize', this.boundHandleResize);
    }

    #isMobileSlider() {
      return window.matchMedia(`(max-width: ${ByteInspirationFeatures.MOBILE_MAX_WIDTH}px)`).matches;
    }

    #updateState() {
      if (!this.track || this.slides.length === 0) return;

      const isMobileSlider = this.#isMobileSlider();
      this.classList.toggle('is-mobile-slider', isMobileSlider);

      if (this.controls) {
        this.controls.hidden = !isMobileSlider || this.slides.length < 2;
      }

      if (!isMobileSlider) {
        this.track.scrollLeft = 0;
        this.activeIndex = 0;
      }

      this.#syncActiveIndexFromScroll();
      this.#updateArrows();
    }

    #syncActiveIndexFromScroll() {
      if (!this.track || this.slides.length === 0) return;

      const trackCenter = this.track.scrollLeft + this.track.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      for (const [index, slide] of this.slides.entries()) {
        const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
        const distance = Math.abs(trackCenter - slideCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      }

      this.activeIndex = closestIndex;
      this.#updateArrows();
    }

    #goToSlide(index) {
      if (index < 0 || index >= this.slides.length) return;

      const targetSlide = this.slides[index];
      if (!targetSlide || !this.track) return;

      this.activeIndex = index;
      this.track.scrollTo({
        left: targetSlide.offsetLeft,
        behavior: 'smooth',
      });
      this.#updateArrows();
    }

    #updateArrows() {
      if (this.prevButton) {
        this.prevButton.disabled = this.activeIndex <= 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = this.activeIndex >= this.slides.length - 1;
      }
    }
  }

  customElements.define('byte-inspiration-features', ByteInspirationFeatures);
}
